import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Element, scroller } from "react-scroll";

// Critical components - loaded immediately (above the fold)
import { NavBar } from "./layout/navbar";
import { PageLoader } from "./components/ui/loading-spinner";
import { addToCart } from "./constants/tier";
import { Hero } from "./components/hero/hero";
import { AmbientColor } from "./components/animations/ambient-color";
import { ModalProvider } from "./components/ui/animated-modal";
import { SpecialOfferBanner } from "./components/hero/SpecialOfferBanner";

// Footer lazy loaded - it's below the fold
const Footer = lazy(() => import("./layout/footer/footer").then(m => ({ default: m.Footer })));
const ScrollProgress = lazy(() => import("./components/animations/scroll-progress"));

// Lazy loaded components - only loaded when needed
const AffiliatePage = lazy(() => import("./affiliate/page"));
const LifetimePage = lazy(() => import("./all-life/LifeTime"));
const LifeTime1Page = lazy(() => import("./all-life/LifeTime1"));
const LoginForm = lazy(() => import("./auth/login"));
const PasswordResetForm = lazy(() => import("./auth/password-reset"));
const MyAccount = lazy(() => import("./my-account/page"));
const HePricing = lazy(() => import("./components/pricing/habrew-pricing"));
const Pricing = lazy(() => import("./components/pricing/pricing"));

// Below the fold components - lazy loaded
const Features = lazy(() => import("./components/features").then(m => ({ default: m.Features })));
const Tools = lazy(() => import("./components/tools/tools").then(m => ({ default: m.Tools })));
const Testimonials = lazy(() => import("./components/testimonials/testimonials").then(m => ({ default: m.Testimonials })));
const QAndA = lazy(() => import("./components/faq/FAQ").then(m => ({ default: m.QAndA })));
const CookieConsent = lazy(() => import("./components/ui/Cookieconsent").then(m => ({ default: m.CookieConsent })));

const App = () => {
  const location = useLocation();
  const currentPath = location.pathname;


  

  const isInitialRender = useRef(true);
  const [prices, setPrices] = useState([]);
  const [showBanner, setShowBanner] = useState(false);

  // Pages that need pricing data
  const needsPrices = [
    "/", "/new-home/", "/wordpress/new-home/",
    "/pricing/", "/wordpress/pricing/",
    "/he-pricing/", "/wordpress/he-pricing/",
    "/lifetime/", "/wordpress/lifetime/",
    "/lifetime1/", "/wordpress/lifetime1/"
  ].includes(currentPath);
  const isHome =
    currentPath === "/" ||
    currentPath === "/new-home/" ||
    currentPath === "/wordpress/new-home/";

  useEffect(() => {
    // Only fetch prices on pages that need them
    if (!needsPrices) return;

    const fetchProductPrices = async () => {
      try {
        const response = await fetch(
          "https://whizmanage.com/wp-json/custom/v1/products"
        );
        const data = await response.json();
        setPrices(data);

        // Check if any product has a discount (any percentage)
        const hasDiscount = data.some((price) => {
          if (price.on_sale && price.sale_price && price.price) {
            const regular = parseFloat(price.price);
            const sale = parseFloat(price.sale_price);
            const discount = Math.round(((regular - sale) / regular) * 100);
            return discount > 0;
          }
          return false;
        });

        setShowBanner(hasDiscount);
      } catch (error) {
        console.error("Failed to fetch product prices: ", error);
      }
    };

    fetchProductPrices();
  }, [needsPrices]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    } else if (
      location.pathname === "/wordpress/new-home/" ||
      ((location.pathname === "/new-home/" || location.pathname === "/") &&
        location.state?.scrollTo)
    ) {
      scroller.scrollTo(location.state.scrollTo, {
        smooth: true,
        duration: 700,
      });
    }
  }, [location]);

  useEffect(() => {
    if (window.isLogin) {
      const productId = localStorage.getItem("productId");
      if (productId) {
        localStorage.removeItem("productId");
        addToCart(productId);
      }
    }
  }, []);

  // Early return AFTER all hooks
if (
  (window.isLocalhost && currentPath === "/wordpress/my-account/") ||
  currentPath === "/my-account/"
) {
  return (
    <Suspense fallback={<PageLoader />}>
      <MyAccount />
    </Suspense>
  );
}

  return (
    <div className="!min-h-screen !min-w-full bg-black flex flex-col justify-between">
      {isHome && (
        <Suspense fallback={null}>
          <ScrollProgress showBanner={showBanner} />
        </Suspense>
      )}

      {/* Special Offer Banner - Fixed at the very top */}
      <SpecialOfferBanner
        prices={prices}
        onClose={() => setShowBanner(false)}
        isVisible={showBanner}
      />

      {/* Add padding top when banner exists - different for mobile */}
      <div className={showBanner ? "pt-[60px] sm:pt-[52px]" : ""}>
        <NavBar />
      </div>

      <Suspense fallback={<PageLoader />}>
        {(window.isLocalhost && currentPath === "/wordpress/new-home/") ||
        currentPath === "/new-home/" ||
        currentPath === "/" ? (
          <div key="home" className="bg-charcoal antialiased w-full ">
            <Element name="top">
              <ModalProvider>
                <div className="relative overflow-visible bg-slate-900">
                  <AmbientColor />
                  <Hero prices={prices} />
                </div>
              </ModalProvider>
            </Element>
            <Element name="features">
              <Features />
            </Element>
            <Element name="tools">
              <Tools />
            </Element>
            <Testimonials />
            <Element name="pricing">
              <Pricing prices={prices} />
            </Element>
            <Element name="faq">
              <QAndA />
            </Element>
          </div>
        ) : (window.isLocalhost && currentPath === "/wordpress/new-login/") ||
          currentPath === "/new-login/" ? (
          <div key="login" className="dark">
            <LoginForm />
          </div>
        ) : (window.isLocalhost &&
            currentPath === "/wordpress/password-reset/") ||
          currentPath === "/password-reset/" ? (
          <div key="password-reset" className="dark">
            <PasswordResetForm />
          </div>
        ) : (window.isLocalhost && currentPath === "/wordpress/affiliates/") ||
          currentPath === "/affiliates/" ? (
          <AffiliatePage />
        ) : (window.isLocalhost && currentPath === "/wordpress/lifetime1/") ||
          currentPath === "/lifetime1/" ? (
          <Suspense fallback={null}>
            <ModalProvider>
              <LifetimePage />
            </ModalProvider>
          </Suspense>
        ) : currentPath === "/wordpress/lifetime/" ||
          currentPath === "/lifetime/" ? (
          <LifeTime1Page />
        ) : (window.isLocalhost && currentPath === "/wordpress/pricing/") ||
          currentPath === "/pricing/" ? (
          <Pricing prices={prices} />
        ) : (window.isLocalhost && currentPath === "/wordpress/he-pricing/") ||
          currentPath === "/he-pricing/" ? (
          <HePricing prices={prices} />
        ) : (
          <div key="else" className="text-white">
            else
          </div>
        )}
      </Suspense>

      <Suspense fallback={<div className="bg-black h-64" />}>
        <div className="bg-black">
          <Footer />
        </div>
      </Suspense>
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
    </div>
  );
};

export default App;
